import {
  clearReviewBinding,
  generateReviewBinding,
  persistReviewBinding,
  readReviewBinding,
  type ReviewBindingStorage,
} from './binding'

export interface ReviewEntry {
  code: string | null
  returnTarget: 'main' | 'portal' | null
  tabBinding?: string
  /** Present only for an entirely query/fragment-free reload. */
  resumeStoredSession?: true
}

interface ReviewEntryRuntime {
  storage?: ReviewBindingStorage | null
  createBinding?(): string
  windowContext?: ReviewWindowContext | null
}

interface ReviewWindowContext {
  opener: unknown
}

const LAUNCH_CODE = /^[A-Za-z0-9_-]{43,128}$/

export function getReviewEntry(search: string, hash: string): ReviewEntry {
  const query = new URLSearchParams(search)
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash
  const params = new URLSearchParams(fragment)
  const codes = params.getAll('launch')
  const targets = params.getAll('return_to')
  const target = targets.length === 0
    ? 'portal'
    : targets.length === 1 && (targets[0] === 'main' || targets[0] === 'portal')
      ? targets[0]
      : null
  // A review bearer in the query has already crossed an HTTP boundary and
  // must never be exchanged. The review entry accepts an entirely query-free
  // URL and only the frozen launch/return keys from the client-only fragment.
  const allowed = [...query.keys()].length === 0 &&
    [...params.keys()].every((key) => key === 'launch' || key === 'return_to')
  const cleanReload = allowed && codes.length === 0 && targets.length === 0
  return {
    code: allowed && codes.length === 1 && LAUNCH_CODE.test(codes[0]) && target
      ? codes[0]
      : null,
    returnTarget: target,
    ...(cleanReload ? { resumeStoredSession: true as const } : {}),
  }
}

export function clearReviewLaunch(
  location: Pick<Location, 'pathname'>,
  history: Pick<History, 'replaceState'>,
) {
  history.replaceState({}, '', location.pathname)
}

export function consumeReviewEntry(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
  history: Pick<History, 'replaceState'>,
  runtime: ReviewEntryRuntime = {},
) {
  const entry = getReviewEntry(location.search, location.hash)
  // Parse into memory and synchronously scrub both query and fragment before
  // loading the review chunk or starting the one allowed exchange request.
  clearReviewLaunch(location, history)
  const storage = runtime.storage === undefined ? browserStorage() : runtime.storage
  const windowContext = runtime.windowContext === undefined
    ? browserWindowContext()
    : runtime.windowContext
  if (entry.code && entry.returnTarget) {
    clearStoredBinding(storage)
    // `window.open()` clones the opener's sessionStorage into a newly created
    // same-origin browsing context. Disown a legitimate launch before
    // persisting its binding so its later reloads remain resumable, while a
    // child opened from the active workspace retains a non-null opener and
    // cannot resume the cloned cookie/binding pair.
    if (!isolateLaunchWindow(windowContext)) return entry
    try {
      const tabBinding = runtime.createBinding?.() ?? generateReviewBinding()
      if (storage && persistReviewBinding(storage, tabBinding, entry.returnTarget)) {
        return { ...entry, tabBinding }
      }
    } catch {
      clearStoredBinding(storage)
    }
    return entry
  }
  if (entry.resumeStoredSession && storage) {
    if (!isOpenerIsolated(windowContext)) {
      clearStoredBinding(storage)
      return entry
    }
    const stored = readReviewBinding(storage)
    if (stored) return {
      ...entry,
      returnTarget: stored.returnTarget,
      tabBinding: stored.binding,
    }
    return entry
  }
  clearStoredBinding(storage)
  return entry
}

function browserStorage(): ReviewBindingStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

function browserWindowContext(): ReviewWindowContext | null {
  return typeof window === 'undefined' ? null : window
}

function isolateLaunchWindow(windowContext: ReviewWindowContext | null) {
  if (!windowContext) return true
  try {
    if (windowContext.opener == null) return true
    windowContext.opener = null
    return windowContext.opener == null
  } catch {
    return false
  }
}

function isOpenerIsolated(windowContext: ReviewWindowContext | null) {
  if (!windowContext) return true
  try {
    return windowContext.opener == null
  } catch {
    return false
  }
}

function clearStoredBinding(storage: ReviewBindingStorage | null) {
  if (storage) clearReviewBinding(storage)
}
